<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb />
      </div>
    </div>
    <b-card>
      <div class="row d-flex mb-4 mb-xl-2 justify-content-between">
        <h4 class="col-12 col-xl-4 mb-2 font-weight-bold">短網址列表</h4>
        <div
          class="
            col-12 col-xl-6
            d-flex
            flex-xl-row
            align-items-start align-items-xl-center
          "
        >
          <b-button
            class="flex-shrink-0 mb-2 mb-xl-0 mr-3"
            variant="primary"
            v-if="checkPermission([consts.SHORT_URL_CREATE])"
            :to="{ name: 'ShortUrlCreate' }"
            ><i class="fa fa-plus"></i>新增短網址</b-button
          >
          <b-input-group>
            <b-form-input
              v-model="query.keyword"
              placeholder="搜尋"
              v-on:keyup.enter="handlePageReset"
            ></b-form-input>
            <b-input-group-append>
              <b-button @click="handlePageReset"
                ><i class="fa fa-search"></i
              ></b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>
      <b-card-text>
        <div class="row">
          <div class="col-12">
            <b-overlay :show="showLoading" rounded="sm">
              <b-table
                striped
                hover
                responsive
                :items="shortUrls"
                :fields="fields"
              >
                <template #cell(code)="data">
                  <b-button
                    v-b-modal="`modal-${data.item.id}`"
                    class="mr-2"
                    variant="inverse-dark"
                  >
                    <i class="fa fa-link mr-0"></i>
                  </b-button>
                  <b-modal :id="`modal-${data.item.id}`">
                    <template #modal-header="{ close }">
                      <h5 class="m-0 align-self-center">
                        {{ data.item.title }}
                      </h5>
                      <b-button
                        size="sm"
                        variant="link"
                        class="text-dark"
                        @click="close()"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 320 512"
                          style="height: 20px"
                        >
                          <path
                            d="M310.6 361.4c12.5 12.5 12.5 32.75 0 45.25C304.4 412.9 296.2 416 288 416s-16.38-3.125-22.62-9.375L160 301.3L54.63 406.6C48.38 412.9 40.19 416 32 416S15.63 412.9 9.375 406.6c-12.5-12.5-12.5-32.75 0-45.25l105.4-105.4L9.375 150.6c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L160 210.8l105.4-105.4c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25l-105.4 105.4L310.6 361.4z"
                          />
                        </svg>
                      </b-button>
                    </template>
                    <div class="d-flex flex-column align-items-center">
                      <b-form-input
                        @click="clipboard(`${shortUrlHost}/${data.item.code}`)"
                        @focus="$event.target.select()"
                        :value="`${shortUrlHost}/${data.item.code}`"
                        v-b-tooltip.click
                        title="已複製"
                      >
                      </b-form-input>
                      <div>
                        <vue-qr
                          :text="`${shortUrlHost}/${data.item.code}`"
                          ref="qrcode"
                        ></vue-qr>
                      </div>
                    </div>
                    <template #modal-footer>
                      <div class="modal-button-group">
                        <b-button
                          variant="primary"
                          class="center-button"
                          @click="downloadQRCode(data.item.title)"
                        >
                          下載QRCode
                        </b-button>
                        <b-button
                          variant="danger"
                          @click="$bvModal.hide(`modal-${data.item.id}`)"
                        >
                          關閉
                        </b-button>
                      </div>
                    </template>
                  </b-modal>
                  <a
                    target="_blank"
                    :href="`${shortUrlHost}/${data.item.code}`"
                    >{{ `${shortUrlHost}/${data.item.code}` }}</a
                  >
                </template>
                <template #cell(url)="data">
                  <div>
                    {{
                      data.item.url_type == "oa"
                        ? "官方帳號"
                        : data.item.url && data.item.url.slice(0, 40) + "..."
                    }}
                  </div>
                </template>
                <template #cell(actions)="data">
                  <b-button
                    variant="inverse-primary"
                    :to="{
                      name: 'ShortUrlView',
                      params: { short_url_id: data.item.id },
                    }"
                  >
                    <span class="mdi mdi-eye"></span> 查看
                  </b-button>
                  <b-button
                    variant="inverse-warning"
                    :to="{
                      name: 'ShortUrlEdit',
                      params: { short_url_id: data.item.id },
                    }"
                  >
                    <span class="mdi mdi-lead-pencil"></span> 編輯
                  </b-button>
                  <b-button
                    variant="inverse-danger"
                    @click="deleteShortUrls(data.item)"
                  >
                    <span class="mdi mdi-delete"></span> 刪除
                  </b-button>
                </template>
              </b-table>
            </b-overlay>
          </div>
        </div>
        <CustomPagination
          :currentPage="query.page"
          :totalRows="totalRows"
          :perPage="query.per_page"
          @page-change="handlePageChange"
          @per-page-change="handlePerPageChange"
        />
      </b-card-text>
    </b-card>
  </div>
</template>

<script>
import { format } from "date-fns";
import { mapState } from "vuex";
import shortUrlApi from "@/apis/short-url";
import PermissionChecker from "@/utils/PermissionChecker";
import * as consts from "@/consts";
import VueQr from "vue-qr";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import { updateQueryFromRoute, updateUrl } from "@/utils/updateUrl";
import { paginationMixin } from "@/mixins/pagination";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  mixins: [paginationMixin],
  components: {
    VueQr,
    CustomPagination,
    BreadCrumb
  },
  data() {
    return {
      consts: consts,
      totalRows: 0,
      showLoading: true,
      shortUrls: [],
      shortUrlHost: process.env.VUE_APP_SHORT_URL_HOST || "https://wal.tw",
      initialized: false,
      query: {
        page: 1,
        per_page: 10,
        keyword: null,
      },
      fields: [
        {
          key: "title",
          label: "標題",
        },
        {
          key: "code",
          label: "短網址",
        },
        {
          key: "url",
          label: "目標網址",
        },
        {
          key: "created_at",
          label: "建立時間",
          formatter: (value) => {
            if (value) {
              return format(new Date(value), "yyyy-MM-dd");
            }
            return "";
          },
        },
        {
          key: "actions",
          label: "操作",
        },
      ],
    };
  },
  computed: {
    ...mapState("general", {
      organization: (state) => state.organization,
    }),
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrl(this.query, this.$store, this.$router);
      },
    },
    $route(to) {
      if (!this.initialized) return;
      this.query = { ...to.query };
      this.getShortUrls();
    },
  },
  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      await this.getShortUrls();
      this.initialized = true;
    },
    async getShortUrls() {
      this.showLoading = true;
      try {
        let params = {
          ...this.query,
        };

        const { data } = await shortUrlApi.getShortUrls(
          this.organization,
          params
        );
        this.shortUrls = data.data;
        this.totalRows = data.meta.total;
      } catch (error) {
        console.error(error);
        if (error.status !== 401) {
          this.$swal.fire({
            title: "錯誤",
            type: "error",
            text: "取得關鍵字資料錯誤",
          });
        }
      }
      this.showLoading = false;
    },
    async deleteShortUrls(shortUrl) {
      await this.$swal
        .fire({
          title: "你確定要刪除嗎？",
          html: `
            <div class="d-block">
              <div class="my-3">
                <div>關鍵字：${shortUrl.code}</div>
              </div>
              <div>刪除後，相關資料將無法回復，<br/>請您確認！</div>
            </div>`,
          type: "warning",
          confirmButtonText: "確定刪除",
          cancelButtonText: "不刪除",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          reverseButtons: true,
        })
        .then(async (result) => {
          if (result.value) {
            let response = await shortUrlApi.removeShortUrl(
              this.organization,
              shortUrl
            );
            if (response.status === 200) {
              this.$swal.fire({
                title: "成功",
                type: "success",
                text: "成功刪除 " + shortUrl.name,
              });
              this.getShortUrls();
            } else {
              if (response.data.message) {
                this.$swal.fire({
                  title: "錯誤",
                  type: "error",
                  text: response.data.message,
                });
              }
            }
          }
        });
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },

    clipboard(value) {
      navigator.clipboard.writeText(value);
    },

    downloadQRCode(filename) {
      const base64QRCodeImage = this.$refs?.qrcode?.$el?.src;
      if (base64QRCodeImage) {
        const temporaryDownloadLink = document.createElement("a");
        temporaryDownloadLink.href = base64QRCodeImage;
        temporaryDownloadLink.download = filename
          ? `${filename}.png`
          : "qrcode.png";
        temporaryDownloadLink.style.display = "none";
        document.body.appendChild(temporaryDownloadLink);
        temporaryDownloadLink.click();
      }
    },
  },
};
</script>

<style scoped>
.copy {
  cursor: pointer;
}

.qrcode {
  cursor: pointer;
}

.btn + .btn {
  margin-left: 0.5rem;
}

.vdp-datepicker {
  min-width: 15rem;
}

.modal-button-group {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  position: relative;
}

.modal-button-group > .center-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
